import {Component, Input, OnInit} from '@angular/core';
import {Faq, FaqApiService, FaqCategory, FaqStatus, KnowledgeDocument, KnowledgeDocumentsApiService, ReferenceBookType} from '@npck/af-frontend-api';
import {rxSpinner, Spinner} from '@npck/libs/commons';
import {toHTML} from 'ngx-editor';
import {combineLatest} from 'rxjs';
import {FilterEvent, FilterField} from '../../../components/full-table/filter/filter.component';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';

const REFRESH_TTL = 90_000;

@Component({
    selector: 'af-knowledge-base-categories',
    templateUrl: 'knowledge-base-categories.component.html',
    styleUrls: ['knowledge-base-categories.component.scss'],
    standalone: false,
})
export class KnowledgeBaseCategoriesComponent implements OnInit {
    @Input() mode: 'faq' | 'documents' = 'faq';

    Spinner = Spinner;
    spinnerId = 'knowledge-base-categories';
    buckets: KnowledgeBaseCategoryViewModel[] = [];

    filterFields: FilterField[] = [{name: 'query', type: 'text'}];
    filterEvent: FilterEvent = {};
    filteredBuckets: KnowledgeBaseCategoryViewModel[] = [];

    constructor(
        private faqApiService: FaqApiService,
        private knowledgeDocumentsApiService: KnowledgeDocumentsApiService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
    }

    get emptyStateTitle(): string {
        const searchKey = 'i18n_af_emptyStateTitleSearchResult';
        if (this.mode === 'documents') {
            return this.filterEvent?.query?.trim() ? searchKey : 'i18n_af_emptyStateTitleKnowledgeDocumentsNotCreated';
        }
        return this.filterEvent?.query?.trim() ? searchKey : 'i18n_af_emptyStateTitleFaqNotCreated';
    }

    get emptyStateSubtitle(): string {
        if (!this.filterEvent?.query?.trim()) {
            return '';
        }
        return this.mode === 'documents'
            ? 'i18n_af_emptyStateSubtitleKnowledgeDocumentsSearchResult'
            : 'i18n_af_emptyStateSubtitleSearchResult';
    }

    get emptyStateIcon(): string {
        return this.filterEvent?.query?.trim() ? 'ic_empty_state_not_found' : 'ic_empty_state_no_docs';
    }

    get bucketSpinnerPrefix(): string {
        return this.mode === 'documents' ? 'knowledge-document-items-' : 'faq-answers-';
    }

    ngOnInit(): void {
        if (this.mode === 'documents') {
            this.loadDocumentBuckets();
        } else {
            this.loadFaqBuckets();
        }
    }

    onFilterChange(event: FilterEvent): void {
        this.filterEvent = event;
        const query = event?.query?.trim();
        if (query) {
            this.mode === 'documents' ? this.searchDocuments(query) : this.searchFaqs(query);
        } else {
            this.buckets.forEach(bucket => {
                bucket.items = [];
                bucket.loadedAt = null;
            });
            this.filteredBuckets = this.buckets;
        }
    }

    onBucketOpened(bucket: KnowledgeBaseCategoryViewModel): void {
        bucket.expanded = true;
        if (bucket.loadedAt && Date.now() - bucket.loadedAt < REFRESH_TTL) {
            return;
        }
        this.mode === 'documents' ? this.loadDocumentItems(bucket) : this.loadFaqItems(bucket);
    }

    onBucketClosed(bucket: KnowledgeBaseCategoryViewModel): void {
        bucket.expanded = false;
    }

    private loadFaqBuckets(): void {
        combineLatest([
            this.faqApiService.getFaqCategories(),
            this.dictionaryHolderService.getReference(ReferenceBookType.FAQ_CATEGORY),
        ])
            .pipe(rxSpinner(this.spinnerId))
            .subscribe(([codes, references]) => {
                this.buckets = codes.map(code => <KnowledgeBaseCategoryViewModel>{
                    code,
                    name: references.find(r => r.code === code)?.name_ru ?? code,
                    expanded: false,
                    items: [],
                    loadedAt: null,
                });
                this.filteredBuckets = this.buckets;
            });
    }

    private loadDocumentBuckets(): void {
        combineLatest([
            this.knowledgeDocumentsApiService.getKnowledgeDocumentTypes(),
            this.dictionaryHolderService.getReference(ReferenceBookType.KNOWLEDGE_BASE_DOCS),
        ])
            .pipe(rxSpinner(this.spinnerId))
            .subscribe(([types, references]) => {
                this.buckets = types.map(type => <KnowledgeBaseCategoryViewModel>{
                    code: type,
                    name: references.find(r => r.code === type)?.name_ru ?? type,
                    expanded: false,
                    items: [],
                    loadedAt: null,
                });
                this.filteredBuckets = this.buckets;
            });
    }

    private loadFaqItems(bucket: KnowledgeBaseCategoryViewModel): void {
        this.faqApiService.getFaqs({
            categories: [bucket.code] as FaqCategory[],
            statuses: [FaqStatus.PUBLISHED],
            hasPlainAnswer: false,
            hasFormattedAnswer: true,
            limit: 20,
            offset: 0,
        })
            .pipe(rxSpinner(this.bucketSpinnerPrefix + bucket.code))
            .subscribe(response => {
                bucket.items = (response.items ?? []).map(item => ({
                    ...item,
                    answer: item.answer ? toHTML(JSON.parse(item.answer as string)) : null,
                }));
                bucket.loadedAt = Date.now();
            });
    }

    private loadDocumentItems(bucket: KnowledgeBaseCategoryViewModel): void {
        this.knowledgeDocumentsApiService.getKnowledgeDocuments({
            types: [bucket.code],
            isArchived: false,
            limit: 200,
            offset: 0,
            sortBy: 'name',
            sortOrder: 'ASC',
        })
            .pipe(rxSpinner(this.bucketSpinnerPrefix + bucket.code))
            .subscribe(response => {
                bucket.items = response.items ?? [];
                bucket.loadedAt = Date.now();
            });
    }

    private searchFaqs(query: string): void {
        this.faqApiService.getFaqs({
            query,
            statuses: [FaqStatus.PUBLISHED],
            hasPlainAnswer: false,
            hasFormattedAnswer: true,
            limit: 200,
            offset: 0,
        })
            .pipe(rxSpinner(this.spinnerId))
            .subscribe(response => {
                const byCategory = new Map<string, Faq[]>();
                (response.items ?? []).forEach(item => {
                    if (item.category) {
                        if (!byCategory.has(item.category)) {
                            byCategory.set(item.category, []);
                        }
                        byCategory.get(item.category).push({
                            ...item,
                            answer: item.answer ? toHTML(JSON.parse(item.answer as string)) : null,
                        });
                    }
                });
                const now = Date.now();
                this.filteredBuckets = this.buckets.filter(b => byCategory.has(b.code));
                this.filteredBuckets.forEach(b => {
                    b.items = byCategory.get(b.code);
                    b.loadedAt = now;
                });
            });
    }

    private searchDocuments(query: string): void {
        this.knowledgeDocumentsApiService.getKnowledgeDocuments({
            name: query,
            isArchived: false,
            limit: 200,
            offset: 0,
        })
            .pipe(rxSpinner(this.spinnerId))
            .subscribe(response => {
                const byType = new Map<string, KnowledgeDocument[]>();
                (response.items ?? []).forEach(item => {
                    if (item.type) {
                        if (!byType.has(item.type)) {
                            byType.set(item.type, []);
                        }
                        byType.get(item.type).push(item);
                    }
                });
                const now = Date.now();
                this.filteredBuckets = this.buckets.filter(b => byType.has(b.code));
                this.filteredBuckets.forEach(b => {
                    b.items = byType.get(b.code);
                    b.loadedAt = now;
                });
            });
    }
}

interface KnowledgeBaseCategoryViewModel {
    code: string;
    name: string;
    expanded: boolean;
    items: Faq[] | KnowledgeDocument[];
    loadedAt: number | null;
}
